import { useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV } from "./navConfig";
import NavDropdown from "./NavDropdown";

const linkTestId = (label) => `nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`;

/** Top-level item: icon + label + chevron, opens its dropdown on hover/focus. */
function DesktopNavItem({ item, open, setOpenKey }) {
  const Icon = item.icon;
  const close = () => setOpenKey(null);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpenKey(item.key)}
      onMouseLeave={close}
      onFocus={() => setOpenKey(item.key)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && close()}
    >
      <NavLink
        to={item.to}
        end={item.to === "/"}
        onClick={close}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={linkTestId(item.label)}
        className={({ isActive }) =>
          `group flex items-center gap-1.5 py-5 text-[13px] 2xl:text-sm font-medium transition-colors ${
            isActive ? "text-topo" : "text-slate-700 hover:text-slate-900"
          }`
        }
      >
        <Icon className="h-4 w-4 text-slate-400 group-hover:text-topo group-aria-[current=page]:text-topo transition-colors" strokeWidth={1.75} />
        <span className="link-underline">{item.label}</span>
        <ChevronDown className={`h-3 w-3 opacity-60 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && <NavDropdown item={item} onNavigate={close} />}
    </div>
  );
}

/** Desktop top-nav (≥xl). Every item has a contextual dropdown. */
export default function DesktopNav({ openKey, setOpenKey }) {
  useEffect(() => {
    if (!openKey) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpenKey(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openKey, setOpenKey]);

  return (
    <nav
      className="hidden xl:flex items-center gap-5 2xl:gap-6"
      data-testid="desktop-nav"
    >
      {NAV.map((item) => (
        <DesktopNavItem key={item.key} item={item} open={openKey === item.key} setOpenKey={setOpenKey} />
      ))}
      <Link
        to="/contact"
        data-testid="nav-cta-quote"
        className="ml-1 inline-flex items-center gap-1.5 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-sm font-semibold px-4 py-2 transition-colors"
      >
        Get a Quote <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </nav>
  );
}
